import { ApiList, ApiOptionList, ApiResponse } from "types/api.types";
import {
  Invoice,
  type InvoiceSummaryData,
  type InvoiceSelectOption,
} from "features/Invoices/types/invoice.types";
import axiosClient from "utils/axios";
import { commaSeparatedEnums, positiveInt } from "utils/list-query";
import {
  InvoiceListSourceTypeParam,
  InvoiceStatus,
} from "features/Invoices/constants/invoice.constants";

const INVOICE_LIST_STATUSES = Object.values(InvoiceStatus);
const INVOICE_LIST_SOURCE_TYPES = Object.values(InvoiceListSourceTypeParam);

function parseOptionalNumber(v: unknown): number | undefined {
  if (v == null || v === "") return undefined;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeInvoiceOptionRow(row: Record<string, unknown>): InvoiceSelectOption {
  const value = Number(row.value);
  return {
    value: Number.isFinite(value) ? value : 0,
    label: String(row.label ?? ""),
    outstandingAmount:
      parseOptionalNumber(row.outstandingAmount) ??
      parseOptionalNumber(row.outstanding_amount),
    sourceType:
      (row.sourceType as string | undefined) ??
      (row.source_type as string | undefined),
    sourceCode:
      (row.sourceCode as string | undefined) ??
      (row.source_code as string | undefined),
    status: (row.status as string | undefined) ?? undefined,
    total: parseOptionalNumber(row.total),
    amountDue:
      parseOptionalNumber(row.amountDue) ??
      parseOptionalNumber(row.amount_due),
  };
}

/** Query payload for `GET /invoices` (list filters + pagination + search). */
export type InvoiceListFilters = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string | string[];
  sourceType?: string | string[];
  sourceId?: number | string;
  clientId?: number | string;
  issueDateFrom?: string;
  issueDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
};

export type GetInvoiceSummaryParams =
  | { year: number; month?: number }
  | { from: string; to: string }
  | Record<string, never>;

export const getInvoiceSummary = async (
  params?: GetInvoiceSummaryParams,
) => {
  const response = await axiosClient.get<ApiResponse<InvoiceSummaryData>>(
    "/invoices/summary",
    { params },
  );
  return response.data;
};

export const getInvoices = async ({
  page = 1,
  limit = 10,
  search,
  status,
  sourceType,
  sourceId,
  clientId,
  issueDateFrom,
  issueDateTo,
  dueDateFrom,
  dueDateTo,
}: InvoiceListFilters = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<Invoice[]>>>(
      "/invoices",
      {
        params: {
          page,
          limit,
          search: search?.trim() || undefined,
          status: commaSeparatedEnums(status, INVOICE_LIST_STATUSES),
          sourceType: commaSeparatedEnums(
            sourceType,
            INVOICE_LIST_SOURCE_TYPES,
          ),
          sourceId: positiveInt(sourceId),
          clientId: positiveInt(clientId),
          issueDateFrom: issueDateFrom?.trim() || undefined,
          issueDateTo: issueDateTo?.trim() || undefined,
          dueDateFrom: dueDateFrom?.trim() || undefined,
          dueDateTo: dueDateTo?.trim() || undefined,
        },
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Invoice list : ", err);
    throw err;
  }
};

export const getInvoice = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<Invoice>>(
      `/invoices/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Invoice details : ", err);
    throw err;
  }
};

export const updateInvoice = async (id: number, payload: FormData) => {
  try {
    const response = await axiosClient.patch<ApiResponse<Invoice>>(
      `/invoices/${id}`,
      payload,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Invoice : ", err);
    throw err;
  }
};

/**
 * Voids the invoice outright (→ CANCELLED). Gated by invoice.cancel. Refused
 * if any direct payment is applied, or if already cancelled. If the only
 * money attached is via advance settlements (no direct payment), those are
 * automatically reversed — the advance itself is unaffected, just freed up
 * to be matched against a different invoice.
 */
export const cancelInvoice = async (
  id: number,
  payload?: { reason?: string | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<Invoice>>(
      `/invoices/${id}/cancel`,
      payload?.reason?.trim() ? { reason: payload.reason.trim() } : {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling Invoice : ", err);
    throw err;
  }
};


export const fetchInvoicePdf = async (id: number): Promise<Blob> => {
  const response = await axiosClient.get(`/invoices/${id}/pdf`, {
    responseType: "blob",
  });
  return response.data as Blob;
};

export const getInvoiceOptions = async ({
  query,
  limit,
  page,
}: {
  query?: string;
  limit?: number;
  page?: number;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/invoices/options`,
      {
        params: {
          query,
          limit,
          page,
        },
      },
    );

    const body = response.data;
    const inner = body.data ?? { rows: [], hasMore: false };
    const rows = (inner.rows ?? []).map((r) =>
      normalizeInvoiceOptionRow(r as unknown as Record<string, unknown>),
    );

    return {
      ...body,
      data: {
        ...inner,
        rows,
      },
    };
  } catch (err) {
    console.warn("Error fetching Equiry Options : ", err);
    throw err;
  }
};
